/**
 * Client-side localisation of backend error messages (`detail` of HTTP errors).
 *
 * The API returns human-readable `detail` strings, some in French, some in
 * English. Rather than changing the API contract, known messages are mapped
 * here to stable i18n keys and rendered in the interface language. Unknown
 * messages (and non-string details) are returned unchanged, so callers keep
 * their existing fallbacks.
 */
import i18n from '@/i18n';

// Exact server messages → key under `serverErrors.`
const EXACT = {
  // Scooter delivery + collection fees (services/delivery_rates.py)
  "La livraison n'est pas proposée pour ce scooter.": 'deliveryNotOffered',
  'Choisissez une zone de livraison desservie par ce loueur.': 'deliveryZoneNotServed',
  'Zone de livraison inconnue.': 'deliveryZoneUnknown',
  "Chaque zone ne peut apparaître qu'une fois.": 'deliveryZoneTwice',
  'Indiquez un tarif en THB pour chaque zone.': 'deliveryFeeMissing',
  "Le tarif d'une zone doit être compris entre 0 et 100000 THB.": 'deliveryFeeRange',
  'Indiquez le nom de votre hôtel ou hébergement pour la livraison.': 'deliveryHotelMissing',
  "Nom d'hébergement trop long.": 'deliveryHotelTooLong',
  'Lien Google Maps invalide : copiez le lien de votre hébergement depuis Google Maps (ex. https://maps.app.goo.gl/…).': 'deliveryMapsInvalid',
  // Commission payments (« Mes commissions Ridalo »)
  'Règlement introuvable.': 'commissionNotFound',
  'Ce règlement est déjà confirmé par Ridalo.': 'commissionAlreadyPaid',
  'Un justificatif est déjà en cours de vérification pour ce règlement.': 'commissionProofPending',
  'Ce justificatif a déjà été envoyé.': 'commissionProofDuplicate',
  'Ce justificatif a déjà été refusé pour ce règlement : envoyez une nouvelle preuve de paiement.': 'commissionProofRejectedAgain',
  "Image trop grande : envoyez la capture d'écran du justificatif.": 'proofTooBig',
  "Image trop petite : envoyez la capture d'écran complète du justificatif.": 'proofTooSmall',
  "Image illisible : envoyez une capture d'écran ou une photo nette du justificatif.": 'proofUnreadable',
  // Vehicle calendar
  "L'heure de fin doit être après l'heure de début.": 'endAfterStartTime',
  'Ce créneau est déjà passé.': 'slotPast',
  'Ce véhicule est en cours de mise à jour. Merci de réessayer dans un instant.': 'vehicleBusyRetry',
  'Heure invalide.': 'invalidTime',
  // Booking life cycle
  "Cette réservation vient d'être modifiée. Merci de recharger la page.": 'bookingChangedMeanwhile',
  "Cette réservation vient d'être modifiée. Actualisez la page.": 'bookingChangedMeanwhile',
  'Cette réservation est déjà annulée.': 'alreadyCancelled',
  "Pour annuler une réservation déjà acceptée, utilisez « Annuler la réservation » et indiquez le motif.": 'cancelAcceptedViaCancel',
  // Private drivers and listing categories
  'Choisissez la durée de mise à disposition du chauffeur.': 'driverDurationRequired',
  "Cette durée n'est pas proposée par ce chauffeur. Choisissez l'une des durées disponibles.": 'driverDurationUnavailable',
  'Ce chauffeur ne propose aucune durée réservable pour le moment.': 'driverNoDuration',
  "Catégorie d'annonce inconnue : cette annonce ne peut pas être réservée.": 'unknownCategory',
  "La catégorie d'une annonce ne peut pas être modifiée.": 'categoryLocked',
  "Indiquez l'heure à laquelle votre véhicule sera de nouveau libre.": 'freeAtRequired',
  'Date ou heure invalide.': 'invalidDateTime',
  "Indiquez le nom de votre hôtel pour la prise en charge.": 'hotelRequired',
  "Ce blocage n'existe plus.": 'blockGone',
  'Ce calendrier ne vous appartient pas.': 'notYourCalendar',
  'Véhicule introuvable.': 'vehicleNotFound',
  "Cette réservation n'occupe plus le véhicule.": 'bookingNoLongerHolds',
  'A review already exists for this booking': 'reviewExists',
  'Au moins un adulte est requis pour réserver une excursion.': 'oneAdultRequired',
  "Aucun acompte de réservation n'est demandé pour cette réservation.": 'noDepositRequired',
  'Aucun historique avec ce client.': 'noCustomerHistory',
  'Aucune adresse email sur votre compte': 'noEmail',
  'Booking not found': 'bookingNotFound',
  'Cannot delete an approved document': 'cannotDeleteApprovedDoc',
  'Cette annonce est actuellement en pause': 'listingPaused',
  "Cette annonce n'est plus disponible": 'listingUnavailable',
  'Cette annonce ne peut pas être soumise dans ce statut': 'listingCannotSubmit',
  'Cette réservation est clôturée en no-show et ne peut plus changer de statut.': 'noShowClosed',
  'Cette réservation est expirée (délai de 24h dépassé) et ne peut plus changer de statut.': 'bookingExpired',
  'Cette réservation ne peut plus être annulée.': 'cannotCancel',
  'Changement de statut non autorisé pour cette réservation.': 'statusChangeNotAllowed',
  'Clients only': 'clientsOnly',
  'Contenu trop long': 'contentTooLong',
  'Dates ou heures invalides': 'invalidDates',
  'Document not found': 'documentNotFound',
  "Documents obligatoires manquants (pièce d'identité, selfie, au moins une photo business).": 'missingDocs',
  'Email already registered': 'emailRegistered',
  'Fichier introuvable ou non autorisé': 'fileNotAllowed',
  'Fichier invalide ou corrompu': 'invalidFile',
  'Fichier vide': 'emptyFile',
  'Format non supporté (JPG, PNG ou WebP uniquement)': 'unsupportedFormat',
  'Format non supporté (JPG, PNG, WebP ou PDF uniquement)': 'unsupportedFormatWithPdf',
  "Format non supporté (JPG, PNG, WebP ou HEIC uniquement)": 'unsupportedFormatWithHeic',
  "Photo HDR non prise en charge. Sur l'iPhone, désactivez le HDR pour cette photo ou enregistrez-la en JPEG, puis réessayez.": 'photoHdrUnsupported',
  "Profil de couleurs de cette photo non pris en charge. Enregistrez-la en JPEG, puis réessayez.": 'photoColourProfileUnsupported',
  "Cette photo HEIC ne peut pas être convertie correctement. Enregistrez-la en JPEG, puis réessayez.": 'heicNotConvertible',
  "Photo trop grande (plus de 50 mégapixels). Réduisez sa taille, puis réessayez.": 'photoTooLarge',
  "Format HEIC momentanément indisponible.": 'heicUnavailable',
  "Photo trop lourde à traiter (résolution et profondeur de couleurs très élevées). Enregistrez-la en JPEG ou réduisez sa taille, puis réessayez.": 'photoTooHeavy',
  'Le serveur traite beaucoup de photos en ce moment. Réessayez dans un instant.': 'photoServerBusy',
  "La plaque d'immatriculation est obligatoire pour publier une annonce de véhicule.": 'plateRequired',
  "Plaque d'immatriculation invalide : lettres thaïes ou latines, chiffres et espaces uniquement (30 caractères maximum).": 'plateInvalid',
  "Impossible de déterminer l'heure de prise en charge.": 'pickupTimeUnknown',
  "L'acompte a déjà été confirmé par le prestataire.": 'depositAlreadyConfirmed',
  'La date/heure de fin doit être postérieure au début': 'endAfterStart',
  'La date/heure de prise en charge est déjà passée (heure de Thaïlande).': 'pickupPassed',
  "Le tarif de l'excursion doit être positif.": 'excursionPricePositive',
  "Le workflow no-show n'est disponible que pour les réservations scooter.": 'noShowScooterOnly',
  "Merci d'indiquer une raison d'annulation.": 'cancelReasonRequired',
  'Merci d\'utiliser le bouton « Client non présenté » dédié pour signaler un no-show.': 'useNoShowButton',
  'Message ou pièce jointe requis': 'messageRequired',
  'No shared booking': 'noSharedBooking',
  'Nom, sujet et message sont obligatoires': 'contactFieldsRequired',
  'Not authenticated': 'notAuthenticated',
  'Not authorized': 'notAuthorized',
  'Only clients can create bookings': 'onlyClientsBook',
  'Only the provider can confirm a deposit': 'onlyProviderConfirmsDeposit',
  'Only the traveler can declare a deposit payment': 'onlyTravelerDeclaresDeposit',
  'Provider access required': 'providersOnly',
  'Provider only': 'providersOnly',
  'Providers only': 'providersOnly',
  'Review not found': 'reviewNotFound',
  'Review only available for completed bookings': 'reviewCompletedOnly',
  'Service not available': 'serviceNotAvailable',
  'Service not found': 'serviceNotFound',
  'Settlement not found': 'settlementNotFound',
  "Seul le destinataire de l'avis peut répondre.": 'onlyRecipientReplies',
  'Seul le prestataire de la réservation peut la marquer en no-show.': 'onlyProviderNoShow',
  'Seuls les voyageurs peuvent devenir prestataires': 'onlyTravelersBecomeProviders',
  'Statut incompatible avec un no-show.': 'noShowIncompatible',
  'Stockage indisponible, réessayez plus tard': 'storageUnavailable',
  'Too many failed attempts. Try again later.': 'tooManyAttempts',
  "Votre profil prestataire doit être validé par l'administration avant de pouvoir publier une offre.": 'profileMustBeApproved',
  'Vous avez déjà répondu à cet avis.': 'alreadyReplied',
  "Vous ne pouvez annuler qu'une réservation en attente.": 'cancelPendingOnly',
  'Vous pourrez signaler un no-show uniquement après l\'heure de prise en charge prévue.': 'noShowAfterPickup',
  'Vérification des fichiers indisponible, réessayez plus tard': 'validatorUnavailable',
  'Your account has been suspended or rejected': 'accountSuspended',
  'Zone de prise en charge requise pour cette excursion.': 'pickupZoneRequired',
  "Type de moyen de paiement invalide.": 'invalidPaymentType',
  "Banque invalide.": 'invalidBank',
  "Nom du titulaire requis.": 'holderRequired',
  // Account deletion requests
  'Une demande de suppression est déjà en cours pour ce compte.': 'deletionDuplicate',
  'Un compte administrateur ne peut pas faire cette demande.': 'deletionAdminAccount',
  'Ce compte est déjà anonymisé.': 'deletionAnonymized',
  "L'adresse de l'expéditeur ne correspond pas à l'adresse e-mail du compte.": 'deletionSenderMismatch',
  "Décrivez la vérification d'identité effectuée et confirmez-la.": 'deletionIdentityCheck',
  'Date de réception invalide.': 'deletionReceivedOn',
  'Compte introuvable.': 'deletionAccountNotFound',
  "Indiquez si le bénéficiaire est un particulier ou une entreprise.": 'beneficiaryType',
  "Nom de famille du bénéficiaire requis.": 'lastNameRequired',
  "Prénom du bénéficiaire requis.": 'firstNameRequired',
  "Nom légal de l'entreprise requis.": 'companyRequired',
  'Province / État requis.': 'provinceRequired',
  'Code SWIFT/BIC invalide (8 ou 11 caractères, code pays TH).': 'invalidSwift',
  "Numéro de compte invalide (10 à 15 chiffres).": 'invalidAccountNumber',
  "Adresse e-mail invalide.": 'invalidEmail',
  "Numéro PromptPay invalide (téléphone thaïlandais à 10 chiffres ou identifiant e-wallet à 15 chiffres).": 'invalidPromptpay',
  "Pour votre sécurité, n'indiquez pas de numéro d'identité national : utilisez le numéro de téléphone lié à PromptPay.": 'noNationalId',
  "Wise : indiquez au moins un moyen de réception (e-mail, identifiant Wise ou coordonnées bancaires).": 'wiseNeedsOne',
  "Revolut : indiquez au moins un moyen de réception (Revtag, téléphone, e-mail ou coordonnées bancaires).": 'revolutNeedsOne',
  "Champ trop long.": 'fieldTooLong',
  "Numéro de téléphone invalide.": 'invalidPhone',
  "Identifiant invalide.": 'invalidTag',
  "Moyen de paiement introuvable.": 'paymentMethodNotFound',
  "Nombre maximum de moyens de paiement atteint (10).": 'maxPaymentMethods',
  "Activez ce moyen de paiement avant de le définir comme principal.": 'activateBeforePrimary',
  "Pour accepter cette réservation, veuillez d'abord ajouter un moyen de paiement.": 'paymentMethodRequired',
  "Le paiement de l'acompte ne peut être déclaré qu'après l'acceptation de la réservation.": 'depositDeclareNotAccepted',
  "Cette réservation n'est plus active.": 'bookingNoLongerActive',
  "Le délai de paiement de l'acompte est dépassé : la déclaration n'est plus possible.": 'depositDeadlinePassed',
  "Un QR code ne peut être associé qu'à un compte bancaire thaïlandais ou à PromptPay.": 'qrWrongType',
  "Image trop petite : le QR code doit mesurer au moins 200 × 200 pixels.": 'qrTooSmall',
  "Image illisible : importez une photo nette ou une capture d'écran de votre QR code.": 'qrUnreadable',
  "QR code introuvable.": 'qrNotFound',
  // Scooter duration discounts (backend/services/scooter_pricing.py)
  '5 paliers de réduction au maximum.': 'pricingTooMany',
  'Le seuil doit être compris entre 2 et 90 jours.': 'pricingDaysRange',
  'La réduction doit être comprise entre 0 et 50 %.': 'pricingPctRange',
  'Le nombre de jours doit être un nombre entier.': 'pricingDaysInteger',
  'La réduction accepte au plus 2 décimales.': 'pricingPctDecimals',
  'Les réductions doivent être une liste.': 'pricingInvalid',
  'Palier de réduction invalide.': 'pricingInvalid',
  'Nombre de jours : valeur invalide.': 'pricingInvalid',
  'Pourcentage : valeur invalide.': 'pricingInvalid',
  "Les réductions en pourcentage ne s'enregistrent qu'avec le mode pourcentage.": 'pricingNeedsPercentMode',
  'Pricing mode only available for scooter category': 'pricingScooterOnly',
  // Airport transfers (backend/services/transfer_booking.py)
  "Choisissez le trajet du transfert.": 'transferRouteRequired',
  "Ce trajet n'est pas proposé par ce prestataire.": 'transferRouteNotServed',
  "Téléphone de contact : champ obligatoire.": 'transferPhoneRequired',
  "Téléphone de contact invalide (indicatif pays conseillé, ex. +66 81 234 5678).": 'transferPhoneInvalid',
  "Nombre de bagages : indiquez un nombre (0 si aucun).": 'transferLuggageNumber',
  "Nombre de bagages : entre 0 et 30.": 'transferLuggageRange',
  "Hôtel ou adresse de destination : champ obligatoire.": 'transferDropoffRequired',
  "Hôtel ou adresse de destination : 200 caractères au maximum.": 'transferDropoffTooLong',
  "Hôtel ou adresse de prise en charge : champ obligatoire.": 'transferPickupRequired',
  "Hôtel ou adresse de prise en charge : 200 caractères au maximum.": 'transferPickupTooLong',
  // Phuket ↔ Krabi transfers (zone → zone)
  'Adresse précise de prise en charge : champ obligatoire.': 'transferPickupExactRequired',
  'Adresse précise de prise en charge : 200 caractères au maximum.': 'transferPickupExactTooLong',
  'Adresse précise de destination : champ obligatoire.': 'transferDropoffExactRequired',
  'Adresse précise de destination : 200 caractères au maximum.': 'transferDropoffExactTooLong',
  'Tarif de transfert Krabi invalide : indiquez un montant en THB.': 'krabiPriceInvalid',
  "Numéro de vol invalide (ex. TG225).": 'transferFlightInvalid',
  "Numéro de chambre : champ obligatoire (ou indiquez que vous ne le connaissez pas encore).": 'transferRoomRequired',
  "Numéro de chambre : 20 caractères au maximum.": 'transferRoomTooLong',
  "Lien Google Maps de votre hôtel : champ obligatoire.": 'transferMapsRequired',
  "Lien Google Maps invalide : copiez le lien de votre hôtel depuis Google Maps (ex. https://maps.app.goo.gl/…).": 'transferMapsInvalid',
  "Le numéro de chambre ne concerne que les transferts hôtel → aéroport.": 'transferRoomOnlyToAirport',
  "Cette réservation ne peut plus être modifiée.": 'transferRoomNotEditable',
  "Cette réservation ne demande pas de numéro de chambre.": 'transferRoomNotAsked',
  'Vous ne pouvez pas réserver votre propre annonce.': 'ownListing',
  'Nombre maximal de passagers : champ obligatoire.': 'listingPassengersMaxRequired',
  'Nombre maximal de bagages : champ obligatoire.': 'listingLuggageMaxRequired',
  'Nombre maximal de passagers : indiquez un nombre entier (1 au minimum).': 'listingPassengersMaxInvalid',
  'Nombre maximal de bagages : indiquez un nombre entier (0 au minimum).': 'listingLuggageMaxInvalid',
  'Nombre de passagers : 1 au minimum.': 'passengersMin',
  'Nombre de passagers : indiquez un nombre entier (1 au minimum).': 'passengersMin',
  "Heure d'arrivée du vol invalide (format HH:MM).": 'transferArrivalInvalid',
  "Numéro de vol : champ obligatoire pour une arrivée à l'aéroport.": 'transferFlightRequired',
  "Heure d'atterrissage prévue : champ obligatoire.": 'transferLandingRequired',
  "Date d'atterrissage prévue : champ obligatoire.": 'transferLandingDateRequired',
  "Date d'atterrissage invalide (format AAAA-MM-JJ).": 'transferLandingDateInvalid',
  'Cette demande a expiré : le délai de réponse est dépassé. Elle ne peut plus être acceptée.': 'transferRequestExpired',
  "Les nouveaux scooters utilisent les réductions en pourcentage : les anciens paliers à prix fixe ne sont plus proposés. Actualisez la page.": 'pricingNewScooterLegacy',
  "Les réductions ne s'enregistrent qu'avec le choix « réductions spécifiques ».": 'pricingDiscountsOnlyCustom',
  "Activez d'abord vos réductions globales.": 'globalDiscountsNotEnabled',
  "Ajoutez au moins une réduction pour activer les réductions globales.": 'globalDiscountsNeedOne',
  "Annonce introuvable : aucune modification effectuée.": 'globalApplyNotFound',
  "Confirmez le remplacement des anciens tarifs : aucune modification effectuée.": 'globalApplyConfirmLegacy',
  "Confirmez le remplacement des réductions spécifiques : aucune modification effectuée.": 'globalApplyConfirmCustom',
};

// Server messages with variable parts: [regex, key, param names]
const PATTERNS = [
  [/^Ce véhicule n'est pas disponible : créneau bloqué par le prestataire \((.+) → (.+)\)\.$/, 'vehicleBlockedSlot', ['from', 'to']],
  [/^Ce véhicule est déjà pris à ce moment \(une demande en attente : (.+)\)\.$/, 'vehicleTakenRequest', ['span']],
  [/^Ce véhicule est déjà pris à ce moment \(une réservation : (.+)\)\.$/, 'vehicleTakenBooking', ['span']],
  [/^Ce créneau contient une demande en attente \(acceptez-la ou refusez-la d'abord\) : (\S+)\.$/, 'blockHasRequest', ['id']],
  [/^Ce créneau contient une réservation : (\S+)\.$/, 'blockHasBooking', ['id']],
  [/^L'heure de fin doit être après l'heure de prise en charge \((\d{2}:\d{2})\)\.$/, 'freeAtBeforePickup', ['time']],
  [/^Un blocage ne peut pas dépasser (\d+) jours\.$/, 'blockTooLong', ['days']],
  [/^Ce véhicule peut accueillir (\d+) passagers? maximum\.$/, 'passengersOverCapacity', ['count']],
  [/^Ce véhicule peut transporter (\d+) bagages? maximum\.$/, 'luggageOverCapacity', ['count']],
  [/^Aucun tarif configuré pour la zone « (.+) »\.$/, 'noRateForZone', ['zone']],
  [/^Cette excursion accepte au maximum (\d+) participants\.$/, 'maxParticipants', ['max']],
  [/^Chevauchement avec (\S+)$/, 'overlapWith', ['id']],
  [/^Créneau bloqué \((.+) → (.+)\)$/, 'slotBlocked', ['from', 'to']],
  [/^Dates indisponibles \((.+)\)\. Le prestataire a bloqué cette période\.$/, 'datesUnavailable', ['date']],
  [/^Fichier trop lourd \(max (\d+) MB\)$/, 'fileTooLarge', ['max']],
  [/^Image trop lourde \(max (\d+) MB\)$/, 'imageTooLarge', ['max']],
  [/^Trop de photos \(max (\d+)\)$/, 'tooManyPhotos', ['max']],
  [/^Impossible de marquer no-show : la réservation est déjà « (.+) »\.$/, 'noShowAlready', ['status']],
  [/^Jour bloqué \((.+)\)\. Retirez d'abord le blocage\.$/, 'dayBlocked', ['date']],
  [/^Ce transfert doit être réservé au moins (\d+) heures à l'avance\.$/, 'transferMinNotice', ['hours']],
  [/^Délai (?:minimum aéroport → hôtel|minimum hôtel → aéroport|maximum de réponse) : nombre entier d'heures entre (\d+) et (\d+)\.$/,
    'transferSettingsRange', ['lo', 'hi']],
  [/^Seuil en double : (\d+) jours\.$/, 'pricingDuplicate', ['days']],
  [/^La réduction ne peut pas diminuer quand la durée augmente \((\d+) j : ([\d.]+) % > (\d+) j : ([\d.]+) %\)\.$/,
    'pricingDecreasing', ['prevDays', 'prevPct', 'days', 'pct']],
];

export const translateServerError = (detail) => {
  if (typeof detail !== 'string') return detail;
  const key = EXACT[detail];
  if (key) return i18n.t(`serverErrors.${key}`);
  for (const [re, k, names] of PATTERNS) {
    const m = re.exec(detail);
    if (m) {
      const params = {};
      names.forEach((name, i) => { params[name] = name === 'count' ? Number(m[i + 1]) : m[i + 1]; });   // count: plural forms
      return i18n.t(`serverErrors.${k}`, params);
    }
  }
  return detail;
};

// Expiry reasons stored by the server (French) → the reader's language.
const EXPIRED_REASONS = {
  'Délai de réponse du prestataire dépassé': 'bookingDetail.expiredReasonProvider',
};
export const translateExpiredReason = (reason) => (EXPIRED_REASONS[reason] ? i18n.t(EXPIRED_REASONS[reason]) : reason);
